import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AGE_OF_MAJORITY, FORMATION_MATRIX, FORMATION_NAMES, LEVY_RAISE_DAYS, MS_PER_GAME_DAY, RESOURCE_KEYS, TROOP_DEFS, TROOP_KEYS, formatDate,
} from '@pk/shared';
import type { ArmyView, Formation, HouseView, Id, Posture, Resources, Troops, WarView, WorldView } from '@pk/shared';
import { actorOf, memberIds, useCharacter, useCommand, useHouse, useWorld } from '../api/hooks';
import { Bar, Badge, Btn, Card, Collapsible, ConfirmRow, Empty, ErrorBox, Field, Inline, Loading, PageHeader, Panel, Screen, ScreenTop, ScrollArea, Section, Sheet, Stat, Tag } from '../components/ui';
import { IconCrown, IconDagger, IconMap, IconPlus, IconShield, IconUsers, IconWar, ResFood, ResGold, ResourceIcon } from '../components/icons';
import { CharLink, TitleLink, canAfford, useEta, useHourEta } from '../components/game';
import { useUi } from '../store';
import { shortestPath } from '../lib/roads';
import { formatEta, msForDays } from '../lib/eta';
import '../styles/screens-war.css';

const fmtUp = (n: number) => (n < 0.05 ? '0' : `-${n < 10 ? n.toFixed(1) : Math.round(n)}`);
const FORMATIONS = Object.keys(FORMATION_NAMES) as Formation[];
const POSTURES: Posture[] = ['hold', 'defend_region', 'aggressive'];
type Cmd = ReturnType<typeof useCommand>;

export function MilitaryScreen() {
  const { t } = useTranslation();
  const house = useHouse();
  const world = useWorld();
  const [raise, setRaise] = useState(false);

  if (house.isLoading || world.isLoading) return <Loading />;
  if (house.isError || world.isError || !house.data || !world.data) return <ErrorBox error={house.error ?? world.error} onRetry={() => { void house.refetch(); void world.refetch(); }} />;
  const h = house.data;
  const w = world.data;
  const mineIds = memberIds(h);
  const levyPool = w.titles.filter((x) => x.holding && x.holderHouseId === h.house.id).reduce((s, x) => s + (x.holding?.levyPool ?? 0), 0);
  const active = h.wars.filter((x) => x.endedDay === null);
  const past = h.wars.filter((x) => x.endedDay !== null);

  const totalTroops = h.armies.reduce((n, a) => n + a.size, 0);
  let upGold = 0;
  let upFood = 0;
  for (const a of h.armies) for (const k of TROOP_KEYS) { upGold += (TROOP_DEFS[k].upkeepGold * a.troops[k]) / 100; upFood += (TROOP_DEFS[k].upkeepFood * a.troops[k]) / 100; }
  const noLand = h.heldTitleIds.length === 0;

  return (
    <Screen className="war-page">
      <ScreenTop>
      <PageHeader icon={<IconWar />} title={t('military.title')} subtitle={t('military.sub')}
        right={<Btn variant="primary" icon={<IconPlus />} disabled={noLand} title={noLand ? t('military.raise.noLand') : undefined} onClick={() => setRaise(true)}>{t('military.raise.button')}</Btn>} />
      </ScreenTop>

      <ScrollArea>
      <div className="war-stats">
        <Stat icon={<IconShield />} label={t('military.sum.armies')} value={h.armies.length} />
        <Stat icon={<IconUsers />} label={t('military.sum.troops')} value={totalTroops} />
        <Stat icon={<IconDagger />} label={t('military.sum.wars')} value={active.length} trend={active.length > 0 ? 'neg' : undefined} />
        <Stat icon={<ResGold />} label={t('military.sum.upkeep')} value={<>{fmtUp(upGold)}<small className="war-sub"> / {fmtUp(upFood)} <ResFood size={14} /></small></>} title={t('military.sum.upkeepHint')} />
      </div>

      <Section title={t('military.armies')} icon={<IconShield />}>
        {!noLand && (
          <p className="war-pool"><ResourceIcon k="manpower" size={20} /> {t('military.levyPool', { n: levyPool })}</p>
        )}
        {h.armies.length === 0 ? (
          <Card className="war-empty">
            <IconShield size={40} />
            <p>{t('military.noArmies')}</p>
            <Btn variant="primary" icon={<IconPlus />} disabled={noLand} onClick={() => setRaise(true)}>{t('military.raise.button')}</Btn>
          </Card>
        ) : <div className="war-grid">{h.armies.map((a) => <ArmyCard key={a.id} army={a} house={h} world={w} />)}</div>}
      </Section>

      <Section title={t('military.wars')} icon={<IconDagger />} right={active.length > 0 ? <Badge tone="red" solid>{active.length}</Badge> : undefined}>
        {active.length === 0 ? <Card tone="green" className="war-peace"><IconCrown size={28} /><b>{t('military.peace')}</b></Card> : <div className="war-grid">{active.map((x) => <WarCard key={x.id} war={x} house={h} world={w} mineIds={mineIds} />)}</div>}
        {past.length > 0 && (
          <Collapsible title={t('military.pastWars')} summary={past.length} className="flush">
            <div className="war-grid">{past.map((x) => <WarCard key={x.id} war={x} house={h} world={w} mineIds={mineIds} />)}</div>
          </Collapsible>
        )}
      </Section>
      </ScrollArea>

      <Sheet open={raise} onClose={() => setRaise(false)} title={t('military.raise.title')}>
        <RaiseForm house={h} levyPool={levyPool} onDone={() => setRaise(false)} />
      </Sheet>
    </Screen>
  );
}

// ------------------------------------------------------------------ raise

function RaiseForm({ house, levyPool, onDone }: { house: HouseView; levyPool: number; onDone: () => void }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const free = house.members.filter((m) => m.alive && m.age >= AGE_OF_MAJORITY && !m.commandingArmyId && !m.prisonerOf);
  const [cmdr, setCmdr] = useState<string>(free.find((m) => m.sex === 'M')?.id ?? '');
  return (
    <div>
      <p className="muted">{t('military.raise.help', { days: LEVY_RAISE_DAYS, min: Math.round((LEVY_RAISE_DAYS * MS_PER_GAME_DAY) / 60_000) })}</p>
      <div className="war-cost">
        <Stat icon={<ResourceIcon k="manpower" />} label={t('military.raise.pool')} value={levyPool} />
        <Stat icon={<ResGold />} label={t('military.raise.upkeep')} value={fmtUp((TROOP_DEFS.levy.upkeepGold * levyPool) / 100)} title={t('military.sum.upkeepHint')} />
        <Stat icon={<ResFood />} label={t('military.raise.upkeep')} value={fmtUp((TROOP_DEFS.levy.upkeepFood * levyPool) / 100)} title={t('military.sum.upkeepHint')} />
      </div>
      <Field label={t('military.commander')}>
        <select className="input" value={cmdr} onChange={(e) => setCmdr(e.target.value)}>
          <option value="">{t('military.noCommander')}</option>
          {free.map((m) => <option key={m.id} value={m.id}>{m.firstName} ({t('attr.martial')} {m.attributes.martial})</option>)}
        </select>
      </Field>
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block busy={cmd.pending} onClick={async () => { const r = await cmd.run({ type: 'raise_army', actorId: actorOf(house), commanderId: cmdr || null }); if (r.ok) onDone(); }}>
          {t('military.raise.submit')}
        </Btn>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ army card

type Dlg = null | 'march' | 'recruit' | 'config' | 'disband';

function ArmyCard({ army, house, world }: { army: ArmyView; house: HouseView; world: WorldView }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  const eta = useEta();
  const hourEta = useHourEta();
  const selected = useUi((s) => s.selectedArmyId === army.id);
  const selectArmy = useUi((s) => s.selectArmy);
  const [dlg, setDlg] = useState<Dlg>(null);
  const cmd = useCommand();
  const actor = memberIds(house).has(army.ownerId) ? army.ownerId : actorOf(house);
  const close = () => { setDlg(null); cmd.clearError(); };
  const dest = army.path.length ? army.path[army.path.length - 1] : null;
  const titleName = (id: Id) => world.titles.find((x) => x.id === id)?.name ?? id;

  const moving = dest && army.nextArrivalDay != null;
  const status = army.siege ? 'besieging' : moving ? 'marching' : 'idle';
  return (
    <Card className={`army-card ${selected ? 'flag-gold' : ''}`}>
      <div className="army-head">
        <span className="army-ico" aria-hidden="true"><IconShield /></span>
        <div className="grow">
          <h3>{t('military.armyOf', { name: army.commanderName ?? army.ownerName })}</h3>
          <div className="army-sub">
            <span className={`status ${status}`}>{t(`military.status.${status}`)}</span> · <TitleLink id={army.locationId} />
          </div>
        </div>
        <Stat icon={<IconUsers />} value={army.size} title={t('map.men', { n: army.size })} />
      </div>
      {army.siege && <Bar label={t('military.siege')} value={Math.round(army.siege.progress * 100)} max={100} tone="red" />}
      {army.siege?.doneHour != null && <div className="small muted">{t('military.siegeDone', { when: hourEta(army.siege.doneHour).full })}</div>}
      <Collapsible title={t('ui.details')} summary={`${t('military.morale')} ${Math.round(army.morale * 100)}`} className="flush">
        <div className="troops">
          {TROOP_KEYS.filter((k) => army.troops[k] > 0).map((k) => (
            <span key={k} className="troop"><span className="num">{army.troops[k]}</span> {TROOP_DEFS[k].name}</span>
          ))}
        </div>
        <Bar label={t('military.morale')} value={Math.round(army.morale * 100)} max={100} tone={army.morale < 0.4 ? 'red' : 'green'} />
        <div className="kv"><span>{t('military.commander')}</span>{army.commanderId ? <CharLink id={army.commanderId} fallback={army.commanderName ?? undefined} /> : <span className="muted">{army.sergeant ? t('military.sergeant') : t('common.none')}</span>}</div>
        {!!army.knights?.length && (
          <div className="kv"><span>{t('military.knights')}</span>
            <span style={{ textAlign: 'right' }}>
              {army.knights.map((k, i) => <span key={k.id}>{i > 0 && ', '}<CharLink id={k.id} fallback={k.name} /></span>)}
              <span className="muted small" style={{ display: 'block' }}>{t('military.knightBonus', { pct: ((army.knightBonus ?? 0) * 100).toFixed(1).replace(/\.0$/, '') })}</span>
            </span>
          </div>
        )}
        <div className="kv"><span>{t('military.stance')}</span><span>{t(`formation.${army.formation}`)} · {t(`posture.${army.posture}`)}</span></div>
        <div className="kv"><span>{t('military.location')}</span><TitleLink id={army.locationId} /></div>
        {moving && (
          <div className="kv"><span>{t('military.marching')}</span>
            <span style={{ textAlign: 'right' }}>
              <TitleLink id={dest} />
              <span className="muted small" style={{ display: 'block' }}>
                {army.path.length > 1 ? t('military.via', { stops: army.path.slice(0, -1).map(titleName).join(' → ') }) + ' · ' : ''}
                {t('military.nextStop', {
                  name: titleName(army.path[0]),
                  date: army.nextArrivalHour != null ? hourEta(army.nextArrivalHour).when : formatDate(army.nextArrivalDay!),
                  eta: army.nextArrivalHour != null ? hourEta(army.nextArrivalHour).eta : eta(army.nextArrivalDay!),
                })}
              </span>
            </span>
          </div>
        )}
      </Collapsible>

      <div className="war-actions">
        <Btn small variant="primary" icon={<IconMap />} onClick={() => { selectArmy(army.id); nav('/map'); }}>{t('military.onMap')}</Btn>
        <Btn small onClick={() => setDlg('march')}>{t('military.march')}</Btn>
        <Btn small onClick={() => setDlg('recruit')}>{t('military.recruit')}</Btn>
        <Btn small onClick={() => setDlg('config')}>{t('military.configure')}</Btn>
        <Btn small variant="quiet" onClick={() => setDlg('disband')}>{t('military.disband')}</Btn>
      </div>

      <Sheet open={dlg === 'march'} onClose={close} title={t('military.march')}>
        {dlg === 'march' && <MarchForm army={army} world={world} actor={actor} cmd={cmd} onDone={close} />}
      </Sheet>
      <Sheet open={dlg === 'recruit'} onClose={close} title={t('military.recruit')}>
        {dlg === 'recruit' && <RecruitForm army={army} house={house} actor={actor} cmd={cmd} onDone={close} />}
      </Sheet>
      <Sheet open={dlg === 'config'} onClose={close} title={t('military.configure')}>
        {dlg === 'config' && <ConfigForm army={army} house={house} actor={actor} cmd={cmd} onDone={close} />}
      </Sheet>
      <Sheet open={dlg === 'disband'} onClose={close} title={t('military.disband')}>
        <ConfirmRow message={t('military.disbandConfirm')} busy={cmd.pending}
          onConfirm={async () => { const r = await cmd.run({ type: 'disband_army', actorId: actor, armyId: army.id }); if (r.ok) close(); }} onCancel={close} />
        <Inline error={cmd.error} />
      </Sheet>
    </Card>
  );
}

function MarchForm({ army, world, actor, cmd, onDone }: { army: ArmyView; world: WorldView; actor: Id; cmd: Cmd; onDone: () => void }) {
  const { t } = useTranslation();
  const [target, setTarget] = useState('');
  const options = useMemo(() => world.titles.filter((x) => x.holding && x.id !== army.locationId).sort((a, b) => a.name.localeCompare(b.name)), [world.titles, army.locationId]);
  const route = useMemo(() => (target ? shortestPath(world.roads, army.locationId, target) : null), [target, world.roads, army.locationId]);
  return (
    <div>
      <p className="muted">{t('military.marchForm.help')}</p>
      <Field label={t('military.marchForm.target')}>
        <select className="input" value={target} onChange={(e) => setTarget(e.target.value)}>
          <option value="">{t('military.marchForm.choose')}</option>
          {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </Field>
      {target && (route
        ? <p className="small">{t('map.eta', { days: route.days, date: formatDate(world.day + route.days), eta: formatEta(msForDays(world, route.days), t as never) })}</p>
        : <p className="small muted">{t('map.noRoad')}</p>)}
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!target || !route} busy={cmd.pending}
          onClick={async () => { const r = await cmd.run({ type: 'move_army', actorId: actor, armyId: army.id, targetId: target }); if (r.ok) onDone(); }}>
          {t('military.marchForm.submit')}
        </Btn>
      </div>
    </div>
  );
}

function RecruitForm({ army, house, actor, cmd, onDone }: { army: ArmyView; house: HouseView; actor: Id; cmd: Cmd; onDone: () => void }) {
  const { t } = useTranslation();
  const [n, setN] = useState<Partial<Troops>>({});
  const types = TROOP_KEYS.filter((k) => k !== 'levy');
  const total: Partial<Resources> = {};
  for (const k of types) {
    const hundreds = (n[k] ?? 0) / 100;
    for (const r of RESOURCE_KEYS) {
      const c = TROOP_DEFS[k].recruit[r];
      if (c) total[r] = (total[r] ?? 0) + c * hundreds;
    }
  }
  const any = types.some((k) => (n[k] ?? 0) > 0);
  const ok = canAfford(house.house.resources, total, 1);
  const bump = (k: keyof Troops, d: number) => setN((s) => ({ ...s, [k]: Math.max(0, (s[k] ?? 0) + d) }));
  return (
    <div>
      <p className="muted">{t('military.recruitForm.help')}</p>
      {types.map((k) => {
        const def = TROOP_DEFS[k];
        const per = RESOURCE_KEYS.filter((r) => def.recruit[r]).map((r) => `${def.recruit[r]} ${t(`resource.${r}`).toLowerCase()}`).join(', ');
        return (
          <div key={k} className="stepper-row">
            <div className="grow">
              <div className="war-troop-name">{def.name}</div>
              <div className="small muted war-per">{RESOURCE_KEYS.filter((r) => def.recruit[r]).map((r) => <span key={r} className={house.house.resources[r] < (def.recruit[r] ?? 0) ? 'lack' : ''}><ResourceIcon k={r} size={16} />{def.recruit[r]}</span>)}<span className="sr-only">{t('military.recruitForm.per', { cost: per })}</span></div>
            </div>
            <div className="stepper">
              <button type="button" aria-label={t('military.recruitForm.less', { name: def.name })} onClick={() => bump(k, -100)} disabled={(n[k] ?? 0) <= 0}>&minus;</button>
              <span className="num">{n[k] ?? 0}</span>
              <button type="button" aria-label={t('military.recruitForm.more', { name: def.name })} onClick={() => bump(k, 100)}>+</button>
            </div>
          </div>
        );
      })}
      <div className="war-total">
        <b>{t('military.recruitForm.total')}</b>
        {any ? RESOURCE_KEYS.filter((r) => (total[r] ?? 0) > 0).map((r) => (
          <span key={r} className={`war-cost-chip ${house.house.resources[r] < (total[r] ?? 0) ? 'lack' : ''}`} title={`${t(`resource.${r}`)}: ${Math.floor(house.house.resources[r])}`}>
            <ResourceIcon k={r} size={20} />{total[r]}<small>/ {Math.floor(house.house.resources[r])}</small>
          </span>
        )) : <span className="muted">{t('common.free')}</span>}
      </div>
      {any && !ok && <p className="inline-error">{t('military.recruitForm.afford')}</p>}
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!any || !ok} busy={cmd.pending}
          onClick={async () => {
            const troops: Partial<Troops> = {};
            for (const k of types) if ((n[k] ?? 0) > 0) troops[k] = n[k];
            const r = await cmd.run({ type: 'recruit', actorId: actor, armyId: army.id, troops });
            if (r.ok) onDone();
          }}>
          {t('military.recruitForm.submit')}
        </Btn>
      </div>
    </div>
  );
}

function matchup(f: Formation, t: (k: string) => string): string {
  const strong = FORMATIONS.filter((o) => FORMATION_MATRIX[f][o] > 1.0).map((o) => t(`formation.${o}`));
  const weak = FORMATIONS.filter((o) => FORMATION_MATRIX[f][o] < 1.0).map((o) => t(`formation.${o}`));
  return `${t('military.config.strong')} ${strong.join(', ') || '-'} · ${t('military.config.weak')} ${weak.join(', ') || '-'}`;
}

function ConfigForm({ army, house, actor, cmd, onDone }: { army: ArmyView; house: HouseView; actor: Id; cmd: Cmd; onDone: () => void }) {
  const { t } = useTranslation();
  const [commander, setCommander] = useState<string>(army.commanderId ?? '');
  const [formation, setFormation] = useState<Formation>(army.formation);
  const [posture, setPosture] = useState<Posture>(army.posture);
  const cands = house.members.filter((m) => m.alive && m.age >= AGE_OF_MAJORITY && !m.prisonerOf && (!m.commandingArmyId || m.commandingArmyId === army.id));
  const changed = commander !== (army.commanderId ?? '') || formation !== army.formation || posture !== army.posture;
  return (
    <div>
      <Field label={t('military.commander')}>
        <select className="input" value={commander} onChange={(e) => setCommander(e.target.value)}>
          <option value="">{t('military.noCommander')}</option>
          {cands.map((m) => <option key={m.id} value={m.id}>{m.firstName} ({t('attr.martial')} {m.attributes.martial})</option>)}
        </select>
      </Field>
      <Field label={t('military.formation')} hint={matchup(formation, t as never)}>
        <select className="input" value={formation} onChange={(e) => setFormation(e.target.value as Formation)}>
          {FORMATIONS.map((f) => <option key={f} value={f}>{t(`formation.${f}`)}</option>)}
        </select>
      </Field>
      <Field label={t('military.posture')} hint={t(`posture.${posture}Help`)}>
        <select className="input" value={posture} onChange={(e) => setPosture(e.target.value as Posture)}>
          {POSTURES.map((p) => <option key={p} value={p}>{t(`posture.${p}`)}</option>)}
        </select>
      </Field>
      <div className="sheet-foot">
        <Inline error={cmd.error} />
        <Btn variant="primary" block disabled={!changed} busy={cmd.pending}
          onClick={async () => {
            const r = await cmd.run({
              type: 'configure_army', actorId: actor, armyId: army.id,
              ...(commander !== (army.commanderId ?? '') ? { commanderId: commander || null } : {}),
              ...(formation !== army.formation ? { formation } : {}),
              ...(posture !== army.posture ? { posture } : {}),
            });
            if (r.ok) onDone();
          }}>
          {t('common.save')}
        </Btn>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ wars

function Side({ id, name }: { id: Id; name: string }) {
  const { t } = useTranslation();
  const c = useCharacter(id);
  return <span><CharLink id={id} fallback={name} />{c.data?.isAI && !c.data.controllable ? <> <Tag title={t('common.aiHint')}>{t('common.ai')}</Tag></> : null}</span>;
}

function WarCard({ war, house, world, mineIds }: { war: WarView; house: HouseView; world: WorldView; mineIds: Set<Id> }) {
  const { t } = useTranslation();
  const cmd = useCommand();
  const eta = useEta();
  const [dlg, setDlg] = useState<null | 'white_peace' | 'surrender'>(null);
  const iAttack = mineIds.has(war.attackerId) || war.attackerAllyIds.some((x) => mineIds.has(x));
  const iLead = mineIds.has(war.attackerId) || mineIds.has(war.defenderId);
  const leader = mineIds.has(war.attackerId) ? war.attackerId : war.defenderId;
  const ended = war.endedDay !== null;
  const mineScore = iAttack ? war.score : -war.score;
  const offered = house.outgoingProposals.find((p) => p.kind === 'peace' && p.warId === war.id && p.status === 'pending');
  const goalTitle = war.goal.titleId ? world.titles.find((x) => x.id === war.goal.titleId) : null;

  return (
    <Card className={`war-card ${ended ? '' : 'flag-red'}`}>
      <div className="army-head">
        <span className={`army-ico ${ended ? '' : 'red'}`} aria-hidden="true"><IconDagger /></span>
        <div className="grow">
          <h3>{war.name}</h3>
          <div className="army-sub">{iAttack ? t('war.attacking') : t('war.defending')}{ended && war.outcome && <> · {t(`war.outcome.${war.outcome}`)}</>}</div>
        </div>
      </div>
      {!ended && war.musterEndsDay !== undefined && war.musterEndsDay > world.day && (
        <p className="small war-note muster-note" role="status">{t(iAttack ? 'war.musterAttacker' : 'war.musterDefender', { date: formatDate(war.musterEndsDay) })}</p>
      )}
      <div className="war-versus">
        <div><span className="small muted">{t('war.attacker')}</span><Side id={war.attackerId} name={war.attackerName} /></div>
        <IconWar size={22} />
        <div><span className="small muted">{t('war.defender')}</span><Side id={war.defenderId} name={war.defenderName} /></div>
      </div>
      <div className="kv"><span>{t('war.goal.label')}</span><span style={{ textAlign: 'right' }}>{t(`war.goal.${war.goal.type}`)}{goalTitle && <> · <TitleLink id={goalTitle.id} /></>}</span></div>
      <Collapsible title={t('ui.details')} summary={war.justified ? t('war.justified') : t('war.unjustified')} className="flush">
        <div className="kv"><span>{t('war.started')}</span><span>{formatDate(war.startDay)}</span></div>
        {(war.attackerAllyIds.length > 0 || war.defenderAllyIds.length > 0) && (
          <div className="kv"><span>{t('war.allies')}</span><span>{t('war.alliesValue', { a: war.attackerAllyIds.length, d: war.defenderAllyIds.length })}</span></div>
        )}
        <div className="tags">
          {war.justified ? <Tag tone="green">{t('war.justified')}</Tag> : <Tag tone="red">{t('war.unjustified')}</Tag>}
          {ended && war.outcome && <Tag tone="blue">{t(`war.outcome.${war.outcome}`)}</Tag>}
        </div>
      </Collapsible>

      {!ended && (
        <>
          <div className="scorebar" role="meter" aria-valuemin={-100} aria-valuemax={100} aria-valuenow={mineScore} aria-label={t('war.score')}><i style={{ left: `${(mineScore + 100) / 2}%` }} /></div>
          <div className="row between small muted war-score"><span>{t('war.losing')}</span><b className={`num ${mineScore > 0 ? 'pos' : mineScore < 0 ? 'neg' : ''}`}>{t('war.score')}: {mineScore > 0 ? '+' : ''}{Math.round(mineScore)}</b><span>{t('war.winning')}</span></div>
          {iLead ? (
            offered ? (
              <p className="small war-note">{t('war.peaceSent', { eta: eta(offered.arriveDay) || '' })}</p>
            ) : (
              <div className="war-actions">
                <Btn small onClick={() => setDlg('white_peace')}>{t('war.whitePeace')}</Btn>
                <Btn small variant="quiet" onClick={() => setDlg('surrender')}>{t('war.surrender')}</Btn>
              </div>
            )
          ) : <p className="small muted" style={{ marginTop: 8 }}>{t('war.notLeader')}</p>}
          <Sheet open={dlg !== null} onClose={() => { setDlg(null); cmd.clearError(); }} title={dlg === 'surrender' ? t('war.surrender') : t('war.whitePeace')}>
            <ConfirmRow message={dlg === 'surrender' ? t('war.surrenderConfirm') : t('war.whitePeaceConfirm')} busy={cmd.pending}
              onConfirm={async () => { const r = await cmd.run({ type: 'offer_peace', actorId: leader, warId: war.id, terms: dlg === 'surrender' ? 'surrender' : 'white_peace' }); if (r.ok) setDlg(null); }}
              onCancel={() => { setDlg(null); cmd.clearError(); }} />
            <Inline error={cmd.error} />
          </Sheet>
        </>
      )}
    </Card>
  );
}
